import React from 'react';
import { useLocation } from 'react-router-dom';

import { PUBLIC_ROUTES } from '~/client/components/public-routes';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';

// List of public route prefixes where we should skip user queries
const PUBLIC_ROUTE_PREFIXES = Object.values(PUBLIC_ROUTES).map((route) =>
  route.split(':')[0].replace(/\/$/, '')
);

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

const SELECTED_ORG_KEY = 'anch_selected_organization_id';

interface AnchContextProps {
  organizations: GQL.AnchOrganizationUnit[];
  stakeholders: GQL.AnchStakeholder[];
  customerId: number | null;

  loading: boolean;
  error: any;

  refetch: () => void;

  selectedOrganization: GQL.AnchOrganizationUnit | null;
  setSelectedOrganization: (org: GQL.AnchOrganizationUnit | null) => void;
  selectedOrganizationId: number | null;
  setSelectedOrganizationId: (id: number | null) => void;

  isConnected: boolean;
}

const DEFAULT_VALUE: AnchContextProps = {
  organizations: [],
  stakeholders: [],
  customerId: null,
  loading: false,
  error: null,
  refetch: () => {},
  selectedOrganization: null,
  setSelectedOrganization: () => {},
  selectedOrganizationId: null,
  setSelectedOrganizationId: () => {},
  isConnected: false,
};

export const AnchContext = React.createContext<AnchContextProps>(DEFAULT_VALUE);

export const useAnchContext = () => {
  const context = React.useContext(AnchContext);
  if (!context) {
    throw new Error('useAnchContext must be used within AnchProvider');
  }
  return context;
};

export const AnchProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Skip user profile fetching on public routes to prevent 401 errors
  const { userProfile, isAuthenticated } = useUserProfile({ skip: isPublic });
  const [selectedOrganization, setSelectedOrganization] =
    React.useState<GQL.AnchOrganizationUnit | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<
    number | null
  >(null);

  const isConnected = Boolean(
    isAuthenticated &&
      userProfile?.anchUserInfo?.token &&
      userProfile?.anchUserInfo?.customerId
  );

  const anchToken = userProfile?.anchUserInfo?.token;

  const [fetchOrganizations, { data, loading, error, refetch }] =
    GQL.useGetAnchOrganizationsLazyQuery({
      fetchPolicy: 'network-only',
    });

  // Handle organization selection after data loads (replaces deprecated onCompleted)
  React.useEffect(() => {
    const orgs =
      data?.getAnchOrganizations?.listCustomerOrganizationUnitResponse ?? [];
    if (orgs.length > 0 && selectedOrganizationId === null) {
      // Check if we have a stored org ID that's still valid
      const storedOrgId = localStorage.getItem(SELECTED_ORG_KEY);
      if (storedOrgId) {
        const storedId = parseInt(storedOrgId, 10);
        // Validate: not NaN and exists in current org list
        if (!isNaN(storedId) && orgs.some((org) => org.id === storedId)) {
          setSelectedOrganizationId(storedId);
          return;
        }
      }
      // Fall back to first org if no valid stored org
      setSelectedOrganizationId(orgs[0].id);
    }
  }, [data, selectedOrganizationId]);

  React.useEffect(() => {
    setSelectedOrganization(null);
    setSelectedOrganizationId(null);

    if (anchToken) {
      fetchOrganizations();
    }
  }, [anchToken, fetchOrganizations]);

  const organizations =
    data?.getAnchOrganizations?.listCustomerOrganizationUnitResponse ?? [];

  // Wrapper to persist org selection to localStorage
  const handleSetSelectedOrganizationId = React.useCallback(
    (id: number | null) => {
      setSelectedOrganizationId(id);
      if (id !== null) {
        localStorage.setItem(SELECTED_ORG_KEY, id.toString());
      } else {
        localStorage.removeItem(SELECTED_ORG_KEY);
      }
    },
    []
  );

  const value = React.useMemo<AnchContextProps>(
    () => ({
      organizations,
      stakeholders:
        data?.getAnchOrganizations?.listCustomerOrganizationUnitStakeHolder ??
        [],
      customerId: data?.getAnchOrganizations?.customerId ?? null,
      loading,
      error,
      refetch,
      selectedOrganization,
      setSelectedOrganization,
      selectedOrganizationId,
      setSelectedOrganizationId: handleSetSelectedOrganizationId,
      isConnected,
    }),
    [
      data,
      loading,
      error,
      refetch,
      selectedOrganization,
      selectedOrganizationId,
      organizations,
      isConnected,
      handleSetSelectedOrganizationId,
    ]
  );

  return <AnchContext.Provider value={value}>{children}</AnchContext.Provider>;
};
